import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import api from './client';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { getResolvedTenantCode } from 'config/resolvedTenant';
import { getTenantCode } from 'config/appConfig';
import { AuthTokens } from 'types/auth';
import { ROUTES } from 'config/routes';
import { tenantStorage } from 'utils/tenantStorage';
import { isAccessJwt, readTenantFromTokenClaims } from 'utils/jwtClaims';
import {
  clearPersistedRefreshTokens,
  sessionRefreshStore,
} from 'utils/sessionRefreshStore';
import authenticationService from 'services/authenticationService';
import storeAdminAuthService from 'services/admin/storeAdminAuthService';

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<AuthTokens> | null = null;

const getRequestUrl = (config?: InternalAxiosRequestConfig): string => {
  const url = String(config?.url || '');
  const base = String(config?.baseURL || '');
  if (!url) return base;
  try {
    return new URL(url, base || window.location.origin).href;
  } catch {
    return `${base}${url}`;
  }
};

const isStoreApiRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/store/v1');
};

const isProductoFotoRequest = (url?: string): boolean => {
  if (!url) return false;
  return /\/api\/productos\/.+\/fotos/i.test(url);
};

const isAdminAreaRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/store/v1/admin/');
};

const isStoreAdminApiRequest = (url?: string): boolean =>
  isAdminAreaRequest(url) || isProductoFotoRequest(url);

const isAdminAuthRequest = (url?: string): boolean => {
  if (!url) return false;
  return /\/admin\/auth\/(login|exchange|logout)/i.test(url);
};

const stripAuthorization = (config: InternalAxiosRequestConfig) => {
  const headers = config.headers as { delete?: (name: string) => void } & Record<string, unknown>;
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
  }
  delete headers.Authorization;
  delete headers.authorization;
};

const attachAdminCsrf = (config: InternalAxiosRequestConfig) => {
  const csrf = storeAdminAuthService.getCsrfToken();
  if (!csrf) return;
  const method = String(config.method || 'get').toUpperCase();
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS' || method === 'TRACE') return;
  config.headers['X-CSRF-TOKEN'] = csrf;
};

/** Sesión admin por cookie HttpOnly: no mandar un Bearer vacío, que pisa la cookie y devuelve 401. */
const useAdminCookie = (config: InternalAxiosRequestConfig, forProductPhotos: boolean) => {
  config.withCredentials = true;
  stripAuthorization(config);
  attachAdminCsrf(config);
  if (forProductPhotos) {
    config.headers['X-Store-Admin'] = '1';
  }
};

const isPublicStoreAuthRequest = (url?: string): boolean => {
  if (!url) return false;
  return /\/auth\/(login|register|forgot-password|reset-password|activate|b2b-access-request)/i.test(url);
};

const attachCustomerCsrf = (config: InternalAxiosRequestConfig) => {
  const csrf = authenticationService.getCsrfToken();
  if (!csrf) return;
  const method = String(config.method || 'get').toUpperCase();
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS' || method === 'TRACE') return;
  config.headers['X-CSRF-TOKEN'] = csrf;
};

const readCustomerJwt = (): string | null => {
  const token = tenantStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!isAccessJwt(token)) return null;
  return token;
};

const readAdminJwt = (): string | null => {
  const adminToken = tenantStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
  if (!isAccessJwt(adminToken)) return null;
  return adminToken;
};

const tokenTenantMatchesResolved = (token: string | null): boolean => {
  if (!token) return true;
  const resolved = getResolvedTenantCode();
  if (!resolved) return true;
  const claims = readTenantFromTokenClaims(token);
  if (!claims) return true;
  return claims.tenantCode.toLowerCase() === resolved.toLowerCase();
};

export const clearSessionAndRedirectToLogin = () => {
  tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
  tenantStorage.removeItem(STORAGE_KEYS.EMAIL);
  tenantStorage.removeItem(STORAGE_KEYS.DISPLAY_NAME);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_COOKIE_SESSION);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_CSRF);
  sessionRefreshStore.clear();
  clearPersistedRefreshTokens();
  delete api.defaults.headers.common.Authorization;
  sessionStorage.setItem('authRedirectReason', 'session_expired');

  if (!window.location.pathname.startsWith(ROUTES.LOGIN)) {
    window.location.replace(ROUTES.LOGIN);
  }
};

export const clearAdminSessionAndRedirect = () => {
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_COOKIE_SESSION);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_CSRF);
  if (!sessionStorage.getItem('authRedirectReason')) {
    sessionStorage.setItem('authRedirectReason', 'session_expired');
  }
  if (!window.location.pathname.startsWith(ROUTES.ADMIN_LOGIN)) {
    window.location.replace(ROUTES.ADMIN_LOGIN);
  }
};

const handleTenantMismatch = (isAdmin: boolean) => {
  sessionStorage.setItem('authRedirectReason', 'tenant_mismatch');
  if (isAdmin) {
    clearAdminSessionAndRedirect();
  } else {
    clearSessionAndRedirectToLogin();
  }
};

const refreshAccessTokenSingleFlight = (): Promise<AuthTokens> => {
  if (!refreshPromise) {
    refreshPromise = authenticationService
      .refreshToken()
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const setupInterceptors = () => {
  clearPersistedRefreshTokens();

  const initialToken = readCustomerJwt();
  if (initialToken && tokenTenantMatchesResolved(initialToken)) {
    api.defaults.headers.common.Authorization = `Bearer ${initialToken}`;
  } else if (tenantStorage.getItem(STORAGE_KEYS.TOKEN)) {
    tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
    tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionRefreshStore.clear();
    delete api.defaults.headers.common.Authorization;
  }

  api.interceptors.request.use((config) => {
    const requestUrl = getRequestUrl(config);
    const tenantCode = getResolvedTenantCode() || getTenantCode();
    if (tenantCode && isStoreApiRequest(requestUrl)) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }

    if (isAdminAreaRequest(requestUrl)) {
      config.withCredentials = true;
      const adminToken = readAdminJwt();
      if (adminToken) {
        if (!tokenTenantMatchesResolved(adminToken)) {
          handleTenantMismatch(true);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        config.headers.Authorization = `Bearer ${adminToken}`;
      } else if (storeAdminAuthService.hasCookieSession() || isAdminAuthRequest(requestUrl)) {
        // El JWT de admin vive en cookie HttpOnly. Un Bearer ausente o "undefined" hace fallar el login.
        useAdminCookie(config, false);
      } else {
        clearAdminSessionAndRedirect();
        return Promise.reject(new Error('Tu sesión expiró. Iniciá sesión de nuevo.'));
      }
    } else if (isProductoFotoRequest(requestUrl) && (readAdminJwt() || storeAdminAuthService.hasCookieSession())) {
      const adminToken = readAdminJwt();
      if (adminToken) {
        if (!tokenTenantMatchesResolved(adminToken)) {
          handleTenantMismatch(true);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        config.headers.Authorization = `Bearer ${adminToken}`;
      } else {
        useAdminCookie(config, true);
      }
    } else if (isStoreApiRequest(requestUrl)) {
      config.withCredentials = true;
      const token = readCustomerJwt();
      if (token) {
        if (!tokenTenantMatchesResolved(token)) {
          handleTenantMismatch(false);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // El JWT de cliente vive en cookie HttpOnly. Sin withCredentials el login no deja sesión.
        stripAuthorization(config);
        attachCustomerCsrf(config);
      }
    } else {
      const token = readCustomerJwt();
      if (token) {
        if (!tokenTenantMatchesResolved(token)) {
          handleTenantMismatch(false);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }

    if (config.data instanceof FormData) {
      const headers = config.headers as { delete?: (name: string) => void } & Record<string, unknown>;
      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type');
      }
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryConfig | undefined;
      const requestUrl = getRequestUrl(originalRequest);
      const requestUrlLower = requestUrl.toLowerCase();
      const isRefresh = requestUrlLower.includes('/auth/refresh');
      const isLogin = /\/admin\/auth\/login|\/auth\/login/i.test(requestUrl);
      const isPublicAuth = isPublicStoreAuthRequest(requestUrl);
      const isAdminRequest = isStoreAdminApiRequest(requestUrl);
      const isAdminAuth = isAdminAuthRequest(requestUrl);

      if (error.response?.status === 503) {
        const data = error.response.data as { code?: string } | undefined;
        if (data?.code === 'store_disabled' && !window.location.pathname.startsWith('/admin')) {
          window.location.replace(ROUTES.STORE_UNAVAILABLE);
        }
      }

      if (error.response?.status === 403) {
        const data = error.response.data as { code?: string } | undefined;
        if (data?.code === 'tenant_mismatch') {
          sessionStorage.setItem('authRedirectReason', 'tenant_mismatch');
          if (isAdminRequest) {
            clearAdminSessionAndRedirect();
          } else {
            clearSessionAndRedirectToLogin();
          }
          return Promise.reject(error);
        }
      }

      if (
        error.response?.status === 401 &&
        !isAdminAuth &&
        isAdminAreaRequest(requestUrl)
      ) {
        clearAdminSessionAndRedirect();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && isRefresh) {
        clearSessionAndRedirectToLogin();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && (isLogin || isPublicAuth)) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAdminRequest) {
        const refreshToken = sessionRefreshStore.get();
        const cookieSession = authenticationService.hasCookieSession();
        if (!refreshToken && !cookieSession) {
          tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
          delete api.defaults.headers.common.Authorization;
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const tokens = await refreshAccessTokenSingleFlight();
          if (isAccessJwt(tokens.token)) {
            originalRequest.headers.Authorization = `Bearer ${tokens.token}`;
          } else {
            stripAuthorization(originalRequest);
            originalRequest.withCredentials = true;
          }
          return api(originalRequest);
        } catch (refreshError) {
          clearSessionAndRedirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
